<template>
    <!-- Where a kit is up to, from the same status and steps as the profile pill's status window
         (or the steps of something made from it, when given a timeline) -->
    <div class="kit-progress">
        <ol class="kp-steps">
            <li v-for="step in steps" :key="step.key" class="kp-step" :class="`kp-step--${step.state}`"
                :aria-current="step.state === 'active' ? 'step' : undefined">
                <span class="kp-step__marker" aria-hidden="true">
                    <svg v-if="step.state === 'done'" width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <svg v-else-if="step.state === 'failed'" width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="3" stroke-linecap="round">
                        <line x1="6" y1="6" x2="18" y2="18" />
                        <line x1="18" y1="6" x2="6" y2="18" />
                    </svg>
                </span>
                <div class="kp-step__body">
                    <strong class="kp-step__name">{{ step.name }}</strong>
                    <span class="visually-hidden">{{ STATE_LABELS[step.state] }}</span>
                    <p class="kp-step__text">{{ step.description }}</p>
                    <p v-if="step.duration" class="kp-step__time">Usually takes {{ step.duration }}</p>
                </div>
            </li>
        </ol>

        <!-- What happens next for a kit that won't produce results: a new sample kit when the API
             allows one, the request already made, or who to talk to -->
        <div v-if="failed && barcode" class="kp-next">
            <RecollectionRequest :barcode="barcode">
                <p class="kp-next__help">
                    If you have any questions, contact
                    <a href="mailto:help@livingdna.com">help@livingdna.com</a>.
                </p>
            </RecollectionRequest>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { kitTimeline, failedStepName } from "@/services/kitService";
import RecollectionRequest from "@/components/RecollectionRequest.vue";

const props = defineProps({
    barcode: { type: String, default: null },
    // The kit's status from the account's tests (raw_status), as the profile pill reads it
    rawStatus: { type: String, default: null },
    // Steps of something other than the kit itself, such as a report made from its results, in
    // kitService's shape ({ completeSteps, activeSteps, failedSteps, incompleteSteps })
    timeline: { type: Object, default: null },
});

const STATE_LABELS = {
    done: "Done",
    active: "In progress",
    upcoming: "Still to come",
    failed: "Failed",
};

const timeline = computed(() => props.timeline ?? kitTimeline(props.barcode, props.rawStatus));

const failed = computed(() => timeline.value.failedSteps.length > 0);

const steps = computed(() => {
    const { completeSteps, activeSteps, failedSteps, incompleteSteps } = timeline.value;
    const toStep = (state) => (step, index) => ({
        key: `${state}-${index}-${step.name}`,
        state,
        name: state === "failed" ? failedStepName(step) : step.name,
        description: step.description,
        // How long a step usually takes only matters for the ones still ahead
        duration: ["active", "upcoming"].includes(state) && step.duration && step.duration !== "-"
            ? step.duration
            : null,
    });
    return [
        ...completeSteps.map(toStep("done")),
        ...activeSteps.map(toStep("active")),
        ...failedSteps.map(toStep("failed")),
        ...incompleteSteps.map(toStep("upcoming")),
    ];
});
</script>

<style lang="scss" scoped>
$blue: #154096;
$blue-soft: #e8edf8;
$green: #10a36b;
$red: #d63c3c;
$ink: #1a1f36;
$ink-3: #4a5472;
$rule: #e6e8ef;

.kit-progress {
    text-align: left;
}

// Phones first: a vertical list, the line running down from each marker to the next
.kp-steps {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
}

.kp-step {
    position: relative;
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr);
    column-gap: 14px;
    padding-bottom: 20px;

    &:last-child {
        padding-bottom: 0;
    }

    &:not(:last-child)::after {
        content: "";
        position: absolute;
        top: 40px;
        bottom: 4px;
        left: 17px;
        width: 2px;
        background: $rule;
    }

    &--done:not(:last-child)::after {
        background: $green;
    }
}

.kp-step__marker {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f4f5f8;
    border: 2px solid $rule;
    color: $ink-3;

    .kp-step--done & {
        background: $green;
        border-color: $green;
        color: #fff;
    }

    .kp-step--active & {
        background: #fff;
        border-color: $blue;
        box-shadow: 0 0 0 4px $blue-soft;

        &::after {
            content: "";
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: $blue;
        }
    }

    .kp-step--failed & {
        background: $red;
        border-color: $red;
        color: #fff;
    }
}

.kp-step__body {
    padding-top: 6px;
    min-width: 0;
}

.kp-step__name {
    display: block;
    font-size: 15px;
    color: $ink;
    margin-bottom: 2px;

    .kp-step--upcoming & {
        color: $ink-3;
        font-weight: 600;
    }

    .kp-step--active & {
        color: $blue;
    }

    .kp-step--failed & {
        color: $red;
    }
}

.kp-step__text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: $ink-3;
}

.kp-step__time {
    margin: 4px 0 0;
    font-size: 13px;
    font-weight: 600;
    color: $ink-3;

    .kp-step--active & {
        color: $blue;
    }
}

// RecollectionRequest brings its own panel
.kp-next {
    margin-top: 20px;
}

.kp-next__help {
    margin: 0;
    font-size: 14px;
    color: $ink;

    a {
        color: $blue;
        font-weight: 600;
    }
}

// Wide screens: the steps run across, the line joining each marker to the next
@media (min-width: 992px) {
    .kp-steps {
        flex-direction: row;
        gap: 16px;
    }

    .kp-step {
        flex: 1 1 0;
        display: block;
        padding-bottom: 0;

        &:not(:last-child)::after {
            top: 17px;
            bottom: auto;
            left: 46px;
            right: -12px;
            width: auto;
            height: 2px;
        }
    }

    .kp-step__marker {
        margin-bottom: 12px;
    }

    .kp-step__body {
        padding-top: 0;
    }
}
</style>
